// v1 placement-redesign: floating row of "+ <field type>" buttons that
// the user clicks to enter "placement mode". After clicking, the next
// click on the PDF canvas places a new marker for the active signer at
// that position. The active button (the one matching the current
// placementMode) is outlined in the active signer's color so the user
// can see what they're about to place and for whom.

import { type CSSProperties } from 'react';
import {
  CalendarDays,
  CheckSquare,
  CircleDot,
  Image,
  Mail,
  PenLine,
  Pencil,
  SquarePen,
  TextCursorInput,
  User,
  Building2,
} from 'lucide-react';
import { APP } from '../app/tokens';
import type { SignerColor } from '../../lib/signerColors';

export type PlacementType = 'signature' | 'date' | 'name' | 'initial' | 'title' | 'email';

export interface AddFieldToolbarProps {
  /** Currently selected placement type (the next page-click will drop one
   *  of these). null = no pending placement. */
  placementMode: PlacementType | null;
  /** Toggle a placement mode. Re-clicking the active type cancels it. */
  onTogglePlacement: (type: PlacementType) => void;
  /** Color of the active signer — used to highlight the active placement
   *  button so the user knows whose marker they're about to drop. */
  activeSignerColor?: SignerColor;
  /** Disable when there are no recipients yet (nothing to attach a marker to). */
  disabled?: boolean;
  /** Compact top-toolbar row or side-rail grid. */
  variant?: 'inline' | 'rail';
}

const TYPES: Array<{
  id: PlacementType;
  label: string;
  Icon: typeof PenLine;
}> = [
  { id: 'signature', label: 'Signature', Icon: PenLine },
  { id: 'initial', label: 'Initials', Icon: SquarePen },
  { id: 'date', label: 'Date signed', Icon: CalendarDays },
  { id: 'name', label: 'Name', Icon: User },
  { id: 'email', label: 'Email', Icon: Mail },
  { id: 'title', label: 'Title', Icon: TextCursorInput },
];

const UPCOMING_TYPES: Array<{
  id: string;
  label: string;
  Icon: typeof PenLine;
}> = [
  { id: 'textbox', label: 'Textbox', Icon: TextCursorInput },
  { id: 'checkbox', label: 'Checkbox', Icon: CheckSquare },
  { id: 'radio', label: 'Radio', Icon: CircleDot },
  { id: 'company', label: 'Company', Icon: Building2 },
  { id: 'editable-date', label: 'Editable date', Icon: CalendarDays },
  { id: 'image', label: 'Image', Icon: Image },
  { id: 'drawing', label: 'Drawing', Icon: Pencil },
];

export default function AddFieldToolbar({
  placementMode,
  onTogglePlacement,
  activeSignerColor,
  disabled,
  variant = 'inline',
}: AddFieldToolbarProps) {
  const rail = variant === 'rail';
  return (
    <div style={rail ? RAIL_GRID : INLINE_WRAPPER}>
      {TYPES.map((t) => {
        const active = placementMode === t.id;
        const accent = activeSignerColor?.outline ?? APP.accentWorkflowPrimary ?? '#D46A3A';
        const Icon = t.Icon;
        return (
          <button
            key={t.id}
            type="button"
            disabled={disabled}
            onClick={() => onTogglePlacement(t.id)}
            title={
              disabled
                ? 'Add at least one recipient first'
                : active
                  ? `Click on the document to place ${t.label.toLowerCase()}, or click again to cancel.`
                  : `Place a new ${t.label.toLowerCase()} field`
            }
            style={{
              ...(rail ? RAIL_BTN : INLINE_BTN),
              borderColor: active ? accent : APP.border,
              color: active ? accent : APP.textPrimary,
              backgroundColor: active ? `${accent}10` : 'transparent',
              fontWeight: active ? 600 : 500,
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Icon size={rail ? 18 : 14} strokeWidth={1.8} />
            <span>+ {t.label}</span>
          </button>
        );
      })}
      {rail && UPCOMING_TYPES.map((t) => {
        const Icon = t.Icon;
        return (
          <button
            key={t.id}
            type="button"
            disabled
            title={`${t.label} fields are coming soon`}
            style={{
              ...RAIL_BTN,
              color: APP.textMuted,
              backgroundColor: APP.bgContent,
              opacity: 0.54,
              cursor: 'not-allowed',
            }}
          >
            <Icon size={18} strokeWidth={1.8} />
            <span>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

const INLINE_WRAPPER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'thin',
  paddingBottom: 1,
};

const INLINE_BTN: CSSProperties = {
  background: 'transparent',
  border: `1px solid ${APP.border}`,
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 12,
  fontFamily: APP.fontFamily,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  transition: 'background-color 120ms, border-color 120ms, color 120ms',
};

const RAIL_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 8,
};

const RAIL_BTN: CSSProperties = {
  background: 'transparent',
  border: `1px solid ${APP.border}`,
  borderRadius: 8,
  minHeight: 72,
  padding: '9px 8px',
  fontSize: 11,
  lineHeight: '14px',
  fontFamily: APP.fontFamily,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  textAlign: 'center',
  transition: 'background-color 120ms, border-color 120ms, color 120ms',
};
